import React, { useState, useEffect } from "react";
import "../styles/AccountHistoryModal.css";
import { usePayment } from "../context/PaymentContext";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from "../utils/numberFormat";

const AccountHistoryModal = ({ onClose }) => {
    const [payments, setPayments] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { openPaymentModal, profile } = usePayment();
    const [tab, setTab] = useState('all'); // all | deposits | withdrawals | cashback | bonus
    const API_URL = getApiUrl();

    useEffect(() => {
        fetchData();
    }, []);

    const { t } = useTranslation("");

    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            const [paymentsRes, withdrawalsPlayerRes, withdrawalsReferalRes, withdrawalsCpaRes, transactionsRes] = await Promise.all([
                fetch(`${API_URL}/api/payments`, { credentials: "include" }),
                fetch(`${API_URL}/api/withdrawals?type=player`, { credentials: "include" }),
                fetch(`${API_URL}/api/withdrawals?type=referal`, { credentials: "include" }),
                fetch(`${API_URL}/api/withdrawals?type=cpa`, { credentials: "include" }),
                fetch(`${API_URL}/api/transactions`, { credentials: "include" })
            ]);
            const paymentsData = await paymentsRes.json();
            const withdrawalsPlayerData = await withdrawalsPlayerRes.json();
            const withdrawalsReferalData = await withdrawalsReferalRes.json();
            const withdrawalsCpaData = await withdrawalsCpaRes.json();
            const transactionsData = await transactionsRes.json();
            // Объединяем обычные, реферальные и CPA выводы
            const allWithdrawals = [
                ...(withdrawalsPlayerData.success ? withdrawalsPlayerData.withdrawals : []),
                ...(withdrawalsReferalData.success ? withdrawalsReferalData.withdrawals : []),
                ...(withdrawalsCpaData.success ? withdrawalsCpaData.withdrawals : [])
            ];
            if (paymentsData.success) setPayments(paymentsData.payments);
            else setError(paymentsData.message || t("errors.paymentsLoad"));
            setWithdrawals(allWithdrawals);
            if (transactionsData.success) setTransactions(transactionsData.transactions);
            else setError(transactionsData.message || t("errors.accountHistoryLoad"));
        } catch {
            setError(t("errors.accountHistoryLoad"));
        } finally {
            setLoading(false);
        }
    };

    // Объединяем и сортируем транзакции
    const allTransactions = [
        ...payments.map(p => ({
            id: p.ext_id,
            type: "deposit",
            date: p.created_at,
            amount: p.amount,
            status: p.status,
            currency: p.currency || 'RUB',
            raw: p,
        })),
        ...withdrawals.map(w => ({
            id: w.ext_id,
            type: "withdrawal",
            date: w.created_at,
            amount: w.amount,
            status: w.status,
            currency: w.currency || 'RUB',
            raw: w,
        })),
        // Добавляем кешбеки и бонусы из транзакций
        ...transactions
            .filter(tx => ["CASHBACK", "BONUS", "RAKEBACK"].includes(tx.transaction_type))
            .map(tx => ({
                id: tx.id,
                type: tx.transaction_type === "CASHBACK" ? "cashback" : 
                     tx.transaction_type === "RAKEBACK" ? "rakeback" : "bonus",
                date: tx.created_at,
                amount: tx.amount,
                status: "SUCCESSFUL",
                currency: tx.currency || tx.raw?.currency || 'RUB',
                raw: tx,
            })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    // Функция для получения символа валюты
    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            case 'EUR': return '€';
            default: return '₽';
        }
    };

    const getTypeLabel = (type, raw) => {
        if (type === "deposit") return t("type.deposit");
        if (type === "withdrawal") {
            if (raw?.type === 'referal') return 'Реферальный вывод';
            if (raw?.type === 'cpa') return 'CPA выплата';
            return t("type.withdrawal");
        }
        if (type === "cashback") return "Кешбек";
        if (type === "rakeback") return "Рейкбек";
        if (type === "bonus") return "Бонус";
        return type;
    };
    const getStatusLabel = (type, status) => {
        if (type === "deposit") {
            if (status === "SUCCESSFUL") return t("deposit.successful");
            if (status === "DENIED") return t("deposit.denied");
            if (status === "pending" || status === "CLIENTPAID") return t("deposit.pending");
            return status;
        } else if (type === "withdrawal") {
            if (status === "WAITING") return t("withdrawal.waiting");
            if (status === "SUCCESSFUL") return t("withdrawal.successful");
            if (status === "DENIED") return t("withdrawal.denied");
            return status;
        } else if (type === "cashback") {
            return "Начислено";
        } else if (type === "rakeback") {
            return "Начислено";
        } else if (type === "bonus") {
            return "Начислено";
        }
        return status;
    };

    // Функция для конвертации времени из UTC+3 в локальное время пользователя
    const convertTimeToUserTimezone = (date) => {
        if (!date) return null;
        const d = new Date(date);
        // Вычитаем 3 часа, так как время в БД в UTC+3
        d.setHours(d.getHours() - 3);
        return d;
    };

    // Функция для получения смещения часового пояса пользователя
    const getUserTimezoneOffset = () => {
        const userTimezone = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const offset = new Date().toLocaleString('ru-RU', { timeZone: userTimezone, timeZoneName: 'shortOffset' }).split(' ').pop();
        return offset.replace('GMT', 'UTC');
    };

    // Вспомогательная функция для форматирования даты (только день, месяц, год)
    const formatDate = (date) => {
        if (!date) return '-';
        const d = convertTimeToUserTimezone(date);
        if (!d) return '-';
        const userTimezone = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        return d.toLocaleString('ru-RU', {
            timeZone: userTimezone,
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    // Вспомогательная функция для форматирования времени
    const formatTime = (date) => {
        if (!date) return '-';
        const d = convertTimeToUserTimezone(date);
        if (!d) return '-';
        const userTimezone = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const time = d.toLocaleString('ru-RU', {
            timeZone: userTimezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        return `${time} ${getUserTimezoneOffset()}`;
    };

    return (
        <div className="profile-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <div className="profile-modal-header">
                    <h3 className="profile-modal-title">{t("AccountHistory.modalTitle")}</h3>
                    <button className="profile-modal-close" onClick={onClose} aria-label={t("accountHistory.close")}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="12" fill="#23232e" />
                            <path d="M16 8L8 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                            <path d="M8 8L16 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <div className="account-history-tabs">
                    <button
                        className={`account-history-tab${tab === 'all' ? ' active' : ''}`}
                        onClick={() => setTab('all')}
                        type="button"
                    >{t("AccountHistory.tabAll")}</button>
                    <button
                        className={`account-history-tab${tab === 'deposits' ? ' active' : ''}`}
                        onClick={() => setTab('deposits')}
                        type="button"
                    >{t("AccountHistory.tabDeposits")}</button>
                    <button
                        className={`account-history-tab${tab === 'withdrawals' ? ' active' : ''}`}
                        onClick={() => setTab('withdrawals')}
                        type="button"
                    >{t("AccountHistory.tabWithdrawals")}</button>
                    <button
                        className={`account-history-tab${tab === 'cashback' ? ' active' : ''}`}
                        onClick={() => setTab('cashback')}
                        type="button"
                    >{t("AccountHistory.tabCashback")}</button>
                    <button
                        className={`account-history-tab${tab === 'rakeback' ? ' active' : ''}`}
                        onClick={() => setTab('rakeback')}
                        type="button"
                    >Рейкбек</button>
                    <button
                        className={`account-history-tab${tab === 'bonus' ? ' active' : ''}`}
                        onClick={() => setTab('bonus')}
                        type="button"
                    >{t("AccountHistory.tabBonus")}</button>
                </div>
                {loading ? (
                    <div className="account-history-loading">{t("AccountHistory.loading")}</div>
                ) : error ? (
                    <div className="account-history-error">{error}</div>
                ) : (
                    <div className="account-history-list">
                        {(() => {
                            let filtered = allTransactions;
                            if (tab === 'deposits') filtered = allTransactions.filter(tx => tx.type === 'deposit');
                            if (tab === 'withdrawals') filtered = allTransactions.filter(tx => tx.type === 'withdrawal');
                            if (tab === 'cashback') filtered = allTransactions.filter(tx => tx.type === 'cashback');
                            if (tab === 'rakeback') filtered = allTransactions.filter(tx => tx.type === 'rakeback');
                            if (tab === 'bonus') filtered = allTransactions.filter(tx => tx.type === 'bonus');
                            if (filtered.length === 0) return <div className="account-history-empty"> {t("AccountHistory.empty")}</div>;
                            return filtered.map((tx, idx) => {
                                // Для кешбека и бонуса не делаем кликабельным
                                const clickable = tx.type === 'deposit' || tx.type === 'withdrawal';
                                const currentDate = formatDate(tx.date);
                                const prevDate = idx > 0 ? formatDate(filtered[idx - 1].date) : null;
                                const showDate = idx === 0 || currentDate !== prevDate;
                                return (
                                    <React.Fragment key={tx.id}>
                                        {showDate && (
                                            <div className="account-history-date-separator">{currentDate}</div>
                                        )}
                                        <div
                                            className={`account-history-item account-history-item--${tx.type}${clickable ? ' account-history-item--clickable' : ''}`}
                                            onClick={clickable ? () => {
                                                onClose();
                                                setTimeout(() => openPaymentModal({
                                                    extId: tx.id,
                                                    type: tx.type === 'deposit' ? 'deposit' : 'withdraw',
                                                    onUpdate: fetchData
                                                }), 0);
                                            } : undefined}
                                            title={clickable ? t("AccountHistory.details") : undefined}
                                        >
                                            <div className="account-history-item-row">
                                                <span className="account-history-type">{getTypeLabel(tx.type, tx.raw)}</span>
                                                <span className="account-history-status">
                                                    {getStatusLabel(tx.type, tx.status)}
                                                    {tx.type === 'withdrawal' && tx.status === 'DENIED' && tx.raw?.admin_comment && (
                                                        <span style={{ marginLeft: '6px', fontSize: '12px' }} title="Есть комментарий администратора">💬</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="account-history-item-row">
                                                <span className="account-history-date">{formatTime(tx.date)}</span>
                                                <span className="account-history-amount account-history-amount--white">{formatBalanceLocale(tx.amount)} {getCurrencySymbol(tx.currency)}</span>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            });
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountHistoryModal; 